// Internal Chat markdown-subset renderer (SPEC-2026-07-internal-chat, T-19).
//
// PURE module — no Angular imports — so the render contract can be validated
// with a plain Node harness (the web app has no spec files / running test
// suite; see T-19 notes). The ChatMarkdownPipe wraps `renderChatMarkdown`.
//
// Supported subset (exactly what the composer toolbar produces):
//   **bold**  _italic_  __underline__  ~~strike~~  [text](https://url)
//   `inline code`  ```code block```  "1. item" ordered / "- item" unordered
//   bare http(s) URLs are auto-linked; everything else renders literally.
//
// SECURITY CONTRACT: the whole input is HTML-escaped FIRST; only the tags
// this renderer emits ever reach the DOM, and anchors are restricted to
// http(s) with target="_blank" rel="noopener noreferrer".

const PLACEHOLDER_MARK = '\u0000';

/** A mention to highlight: the display name as inserted by the composer. */
export interface ChatMentionRender {
  name: string;
  /** The mention targets the viewing user — gets the stronger tint. */
  self?: boolean;
}

interface ProtectedChunk {
  token: string;
  html: string;
  /** block-level chunks (pre) suppress the surrounding <br> handling */
  block: boolean;
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Inline emphasis over already-escaped text (code/links protected upstream). */
function renderInline(text: string): string {
  let out = text;
  // Order matters: ** before __ before _ (so __u__ never half-matches _i_).
  out = out.replace(/\*\*([^\n]+?)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^\n]+?)__/g, '<u>$1</u>');
  out = out.replace(/~~([^\n]+?)~~/g, '<del>$1</del>');
  // Italic requires soft word boundaries so snake_case tokens stay literal.
  out = out.replace(/(^|[\s(])_([^\n_]+)_(?=$|[\s).,;:!?])/g, '$1<em>$2</em>');
  return out;
}

function escapeRegExp(term: string): string {
  return term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Renders the chat markdown subset to sanitized HTML.
 * Line breaks are preserved (<br>); consecutive "1. " / "- " lines become
 * real <ol>/<ul> lists; links always open in a new tab with rel=noopener.
 *
 * `mentions` (optional) highlights `@Name` occurrences — the exact tokens the
 * composer inserts and the send path detects (`content.includes('@' + name)`).
 * Names are matched over the ESCAPED text and the resulting span is protected,
 * so the emphasis passes never restyle a name's inner characters.
 */
export function renderChatMarkdown(content: string, mentions?: ChatMentionRender[]): string {
  // The placeholder mark is a control char users cannot type — strip any
  // occurrence so crafted input can never address a protected chunk.
  let text = escapeHtml((content ?? '').replace(new RegExp(PLACEHOLDER_MARK, 'g'), ''));

  const chunks: ProtectedChunk[] = [];
  const protect = (html: string, block: boolean): string => {
    const token = `${PLACEHOLDER_MARK}${chunks.length}${PLACEHOLDER_MARK}`;
    chunks.push({ token, html, block });
    return token;
  };

  // 1) Fenced code blocks — inner content stays verbatim (no inline styling).
  text = text.replace(/```([\s\S]*?)```/g, (_match, inner: string) => {
    const code = String(inner).replace(/^\n/, '').replace(/\n$/, '');
    return protect(`<pre class="chat-md-pre"><code>${code}</code></pre>`, true);
  });

  // 2) Inline code spans.
  text = text.replace(/`([^`\n]+)`/g, (_match, inner: string) =>
    protect(`<code>${inner}</code>`, false));

  // 3) Markdown links — http(s) only; URL/label are already escaped.
  text = text.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s()<]+)\)/g, (_match, label: string, url: string) =>
    protect(`<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`, false));

  // 4) Auto-link remaining bare URLs.
  text = text.replace(/(https?:\/\/[^\s<]+)/g, (url: string) =>
    protect(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`, false));

  // 4b) Mention chips. Longest name first, so "@Ana Paula" is claimed whole
  // before a shorter "@Ana" can split it. Names are escaped exactly like the
  // surrounding text was (step 0), so the literal match holds for names with
  // `&`/`<`/quotes in them.
  for (const mention of [...(mentions ?? [])].sort((a, b) => b.name.length - a.name.length)) {
    const name = escapeHtml(mention.name ?? '');
    if (!name) continue;
    const cssClass = mention.self ? 'chat-md-mention chat-md-mention--self' : 'chat-md-mention';
    text = text.replace(
      new RegExp(`@${escapeRegExp(name)}`, 'g'),
      () => protect(`<span class="${cssClass}">@${name}</span>`, false),
    );
  }

  // 5) Inline emphasis on what is left.
  text = renderInline(text);

  // 6) Line assembly: lists, block chunks and <br> handling.
  const blockTokenOnly = new RegExp(`^${PLACEHOLDER_MARK}(\\d+)${PLACEHOLDER_MARK}$`);
  const html: string[] = [];
  let listType: 'ol' | 'ul' | null = null;
  let needsBreak = false;

  const closeList = (): void => {
    if (!listType) return;
    html.push(listType === 'ol' ? '</ol>' : '</ul>');
    listType = null;
  };

  for (const line of text.split('\n')) {
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    const unordered = /^\s*-\s+(.*)$/.exec(line);

    if (ordered || unordered) {
      const type: 'ol' | 'ul' = ordered ? 'ol' : 'ul';
      if (listType && listType !== type) closeList();
      if (!listType) {
        html.push(type === 'ol' ? '<ol class="chat-md-list">' : '<ul class="chat-md-list">');
        listType = type;
      }
      html.push(`<li>${(ordered ?? unordered)[1]}</li>`);
      needsBreak = false;
      continue;
    }

    closeList();

    const blockMatch = blockTokenOnly.exec(line.trim());
    if (blockMatch && chunks[Number(blockMatch[1])]?.block) {
      html.push(line.trim());
      needsBreak = false;
      continue;
    }

    if (needsBreak) html.push('<br>');
    html.push(line);
    needsBreak = true;
  }
  closeList();

  // 7) Restore protected chunks (reverse order safe — tokens are unique).
  let out = html.join('');
  for (const chunk of chunks) {
    out = out.replace(chunk.token, chunk.html);
  }
  return out;
}
